// src/components/Chat/components/ChatUI/ChatInput.jsx
import React, { useState, useRef } from "react";
import { FiSend, FiPaperclip, FiSmile } from "react-icons/fi";

const ChatInput = ({
  disabled,
  onSend,
  onTyping,
  onFileSelect,
  fileInputRef,
}) => {
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const handleChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    // Handle typing indicator
    if (!isTyping && value.trim()) {
      setIsTyping(true);
      onTyping?.(true); // ✅ This sends to other user only
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        onTyping?.(false); // ✅ Stop typing for other user
      }
    }, 1000);
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || disabled) return;

    onSend(trimmed);
    setMessage("");

    // Clear typing
    if (isTyping) {
      setIsTyping(false);
      onTyping?.(false);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect?.(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="border-t-2 border-yellow-300 bg-gradient-to-r from-blue-50 to-blue-100 p-3">
      <div className="flex items-center gap-2">
        {/* File attachment button */}
        <button
          onClick={handleFileClick}
          disabled={disabled}
          className="p-2.5 rounded-full bg-blue-900 text-yellow-400 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          title="Attach file"
        >
          <FiPaperclip className="text-lg" />
        </button>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />

        {/* Message input */}
        <div className="flex-1 relative">
          <textarea
            value={message}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder={disabled ? "Connecting..." : "Type a message..."}
            disabled={disabled}
            rows={1}
            className="w-full bg-white border-2 border-blue-900 rounded-xl py-3 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-none max-h-32 text-sm font-medium text-gray-900 placeholder-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={() => {}}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-900 hover:text-blue-700"
          >
            <FiSmile className="text-lg" />
          </button>
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          className="p-2.5 rounded-full bg-gradient-to-br from-blue-900 to-blue-950 text-yellow-400 hover:from-blue-800 hover:to-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          title="Send message"
        >
          <FiSend className="text-lg" />
        </button>
      </div>

      {/* Helper text */}
      {/* <div className="mt-2 text-center">
        <p className="text-xs text-blue-700 font-semibold">
          {disabled ? "🔴 Connecting to server..." : "🟢 Connected"}
        </p>
      </div> */}
    </div>
  );
};

export default ChatInput;
